<template>
  <div class="trailer-service">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>尾程管理</el-breadcrumb-item>
      <el-breadcrumb-item>拖车服务</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card class="el-card-border" style="margin-top: 16px">
      <div slot="header" class="clearfix">
        <b>提交拖车服务</b>
        <span style="margin-left: 12px; color: #909399; font-size: 13px">
          有 PU/DO·DP 单据可直接约车；没有则先排队，放行后再确认时间
        </span>
      </div>
      <el-form :model="form" label-width="110px" style="max-width: 720px">
        <el-form-item label="PU号">
          <el-input v-model="form.puNo" maxlength="64" clearable placeholder="有 PU 直接填 → 可约"></el-input>
        </el-form-item>
        <el-form-item label="柜号">
          <el-input v-model="form.containerNo" maxlength="50" clearable placeholder="可选"></el-input>
        </el-form-item>
        <el-form-item label="提单号">
          <el-input v-model="form.blNo" maxlength="50" clearable placeholder="MBL / 提单号，可选"></el-input>
        </el-form-item>
        <el-form-item label="送仓地址" required>
          <el-input
            type="textarea"
            :rows="2"
            v-model="form.deliveryAddress"
            maxlength="500"
            placeholder="必填"
          ></el-input>
        </el-form-item>
        <el-form-item label="期望送仓" required>
          <el-date-picker
            v-model="form.deliverPlanDate"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="选择日期"
            style="width: 180px; margin-right: 8px"
          ></el-date-picker>
          <el-radio-group v-model="form.deliverPlanSlot">
            <el-radio-button label="AM">上午</el-radio-button>
            <el-radio-button label="PM">下午</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="提柜港口/堆场" required>
          <el-input
            v-model="form.railRampAddress"
            maxlength="200"
            clearable
            placeholder="必填，如 美中火车站BNSF / LA码头"
          ></el-input>
        </el-form-item>
        <el-form-item label="件数" required>
          <el-input-number v-model="form.packageCount" :min="1" :max="99999" :step="1"></el-input-number>
          <span style="margin-left: 8px; color: #909399; font-size: 12px">必填，柜子里一共多少件</span>
        </el-form-item>
        <el-form-item label="数量/箱数">
          <el-input-number v-model="form.cargoQty" :min="0" :max="99999" :step="1"></el-input-number>
          <span style="margin-left: 8px; color: #909399; font-size: 12px">选填</span>
        </el-form-item>
        <el-form-item label="卸货方式">
          <el-radio-group v-model="form.unloadMode">
            <el-radio-button label="DROP">Drop 留柜（放下就走）</el-radio-button>
            <el-radio-button label="LIVE">Live 现场卸（司机等卸完）</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="备注">
          <el-input
            type="textarea"
            :rows="2"
            v-model="form.remark"
            maxlength="1000"
            placeholder="查验说明、DP 单号等"
          ></el-input>
        </el-form-item>
        <el-form-item label="单据附件">
          <el-upload
            action="#"
            :http-request="uploadFile"
            :file-list="fileList"
            :on-remove="onRemove"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
          >
            <el-button size="small" type="primary">上传提柜单/DP/查验单</el-button>
            <div slot="tip" class="el-upload__tip">有附件或 PU → 可约；否则进入排队</div>
          </el-upload>
        </el-form-item>
        <el-form-item>
          <el-button type="warning" :loading="submitting" @click="submit">提交</el-button>
          <el-button @click="resetForm">清空</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card class="el-card-border" style="margin-top: 16px">
      <div slot="header" class="clearfix">
        <b>我的拖车单</b>
        <el-button style="float: right" type="text" @click="loadList">刷新</el-button>
      </div>
      <el-table :data="rows" stripe v-loading="loading">
        <el-table-column prop="planName" label="货柜代号" width="130"></el-table-column>
        <el-table-column prop="puNo" label="PU" width="100" show-overflow-tooltip></el-table-column>
        <el-table-column prop="containerNo" label="柜号" width="120" show-overflow-tooltip></el-table-column>
        <el-table-column label="期望送仓" width="140">
          <template slot-scope="scope">
            {{ formatPlanDay(scope.row.deliverPlanTime) }}
            {{ scope.row.deliverPlanSlot === 'PM' ? '下午' : scope.row.deliverPlanSlot === 'AM' ? '上午' : '' }}
          </template>
        </el-table-column>
        <el-table-column label="状态" width="120">
          <template slot-scope="scope">
            <el-tag :type="stageTagType(scope.row.trailerStage)" size="mini">
              {{ stageLabel(scope.row) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="总费用" width="100" align="right">
          <template slot-scope="scope">
            <span>{{ formatMoney(scope.row.money) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="支付状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="payTagType(scope.row.payStatus)" size="mini">
              {{ payStatusText(scope.row.payStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="deliveryAddress" label="送仓地址" min-width="140" show-overflow-tooltip></el-table-column>
        <el-table-column label="提柜港口/堆场" width="150" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.railRampAddress || scope.row.pickupStation || '-' }}</template>
        </el-table-column>
        <el-table-column label="件数" width="90" align="right">
          <template slot-scope="scope">
            <span>{{ scope.row.packageCount != null ? scope.row.packageCount : '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="卸货方式" width="110">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.unloadMode === 'LIVE'" type="warning" size="mini">Live 现场卸</el-tag>
            <el-tag v-else-if="scope.row.unloadMode === 'DROP'" size="mini">Drop 留柜</el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.trailerStage === 'QUEUE'"
              type="text"
              size="mini"
              @click="openRelease(scope.row)"
            >已放行·补传</el-button>
            <el-button
              v-if="scope.row.trailerStage === 'CONFIRM' || scope.row.trailerStage === 'QUEUE'"
              type="text"
              size="mini"
              @click="openConfirm(scope.row)"
            >确认时间</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div style="margin-top: 12px; text-align: right">
        <el-pagination
          @current-change="onPage"
          @size-change="onSize"
          :current-page="page"
          :page-sizes="[10, 20, 50]"
          :page-size="rowsPerPage"
          layout="total, sizes, prev, pager, next"
          :total="total"
        ></el-pagination>
      </div>
    </el-card>

    <el-dialog title="补传放行单据" :visible.sync="releaseVisible" width="480px">
      <el-upload
        action="#"
        :http-request="uploadReleaseFile"
        :file-list="releaseFileList"
        :on-remove="onReleaseRemove"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
      >
        <el-button size="small" type="primary">上传 DO/DP</el-button>
      </el-upload>
      <span slot="footer">
        <el-button @click="releaseVisible = false">取消</el-button>
        <el-button type="warning" :loading="actionLoading" @click="doRelease">提交放行</el-button>
      </span>
    </el-dialog>

    <el-dialog title="确认送仓时间" :visible.sync="confirmVisible" width="480px">
      <el-form label-width="100px">
        <el-form-item label="送仓日期">
          <el-date-picker
            v-model="confirmForm.deliverPlanDate"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="选择日期"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="时段">
          <el-radio-group v-model="confirmForm.deliverPlanSlot">
            <el-radio-button label="AM">上午</el-radio-button>
            <el-radio-button label="PM">下午</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="提柜港口/堆场">
          <el-input v-model="confirmForm.railRampAddress" maxlength="200" clearable placeholder="补传可改"></el-input>
        </el-form-item>
        <el-form-item label="件数">
          <el-input-number v-model="confirmForm.packageCount" :min="1" :max="99999" :step="1"></el-input-number>
        </el-form-item>
        <el-form-item label="卸货方式">
          <el-radio-group v-model="confirmForm.unloadMode">
            <el-radio-button label="DROP">Drop 留柜</el-radio-button>
            <el-radio-button label="LIVE">Live 现场卸</el-radio-button>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="confirmVisible = false">取消</el-button>
        <el-button type="warning" :loading="actionLoading" @click="doConfirmTime">确认并约车</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import httpApi from "@/http/api";

export default {
  name: "TrailerService",
  data() {
    return {
      form: {
        puNo: "",
        containerNo: "",
        blNo: "",
        deliveryAddress: "",
        railRampAddress: "",
        packageCount: undefined,
        cargoQty: undefined,
        unloadMode: "DROP",
        deliverPlanDate: "",
        deliverPlanSlot: "AM",
        remark: "",
      },
      fileList: [],
      uploaded: [],
      submitting: false,
      loading: false,
      rows: [],
      page: 1,
      rowsPerPage: 20,
      total: 0,
      releaseVisible: false,
      confirmVisible: false,
      actionLoading: false,
      currentRow: null,
      releaseFileList: [],
      releaseUploaded: [],
      confirmForm: {
        deliverPlanDate: "",
        deliverPlanSlot: "AM",
        railRampAddress: "",
        packageCount: undefined,
        unloadMode: "DROP",
      },
    };
  },
  created() {
    this.loadList();
  },
  methods: {
    stageLabel(row) {
      if (row.trailerStageTxt) return row.trailerStageTxt;
      const map = {
        QUEUE: "排队中",
        CONFIRM: "请确认时间",
        READY: "已接单待派",
        DISPATCHED: "已派车",
        DONE: "已完成",
      };
      return map[row.trailerStage] || row.truckStatusTxt || "-";
    },
    stageTagType(stage) {
      if (stage === "QUEUE") return "info";
      if (stage === "CONFIRM") return "warning";
      if (stage === "READY") return "";
      if (stage === "DISPATCHED") return "success";
      if (stage === "DONE") return "success";
      return "info";
    },
    formatMoney(money) {
      if (money === null || money === undefined || money === "") return "-";
      const n = Number(money);
      if (Number.isNaN(n)) return "-";
      return n.toFixed(2);
    },
    payStatusText(status) {
      return status || "未支付";
    },
    payTagType(status) {
      const s = status || "未支付";
      if (s === "已支付") return "success";
      if (s === "部分支付") return "warning";
      if (s === "未支付") return "danger";
      return "info";
    },
    formatPlanDay(ts) {
      if (!ts) return "-";
      const d = new Date(ts * 1000);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    uploadFile(req) {
      const fd = new FormData();
      fd.append("file", req.file);
      return httpApi
        .trailerUpload(fd)
        .then((res) => {
          if (res.data.code !== "ok") {
            this.$message.warning(res.data.message || "上传失败");
            return;
          }
          const d = res.data.data || {};
          this.uploaded.push({ url: d.url, name: d.name || req.file.name });
          this.fileList.push({
            name: d.name || req.file.name,
            url: d.url,
          });
          this.$message.success("上传成功");
        })
        .catch(() => this.$message.error("上传失败"));
    },
    onRemove(file) {
      const url = file.url;
      this.uploaded = this.uploaded.filter((x) => x.url !== url);
      this.fileList = this.fileList.filter((x) => x.url !== url);
    },
    uploadReleaseFile(req) {
      const fd = new FormData();
      fd.append("file", req.file);
      return httpApi
        .trailerUpload(fd)
        .then((res) => {
          if (res.data.code !== "ok") {
            this.$message.warning(res.data.message || "上传失败");
            return;
          }
          const d = res.data.data || {};
          this.releaseUploaded.push({ url: d.url, name: d.name || req.file.name });
          this.releaseFileList.push({ name: d.name || req.file.name, url: d.url });
        })
        .catch(() => this.$message.error("上传失败"));
    },
    onReleaseRemove(file) {
      const url = file.url;
      this.releaseUploaded = this.releaseUploaded.filter((x) => x.url !== url);
      this.releaseFileList = this.releaseFileList.filter((x) => x.url !== url);
    },
    resetForm() {
      this.form = {
        puNo: "",
        containerNo: "",
        blNo: "",
        deliveryAddress: "",
        railRampAddress: "",
        packageCount: undefined,
        cargoQty: undefined,
        unloadMode: "DROP",
        deliverPlanDate: "",
        deliverPlanSlot: "AM",
        remark: "",
      };
      this.fileList = [];
      this.uploaded = [];
    },
    submit() {
      const hasKey =
        (this.form.puNo && this.form.puNo.trim()) ||
        (this.form.containerNo && this.form.containerNo.trim()) ||
        (this.form.blNo && this.form.blNo.trim()) ||
        (this.form.remark && this.form.remark.trim()) ||
        this.uploaded.length > 0;
      if (!hasKey) {
        this.$message.warning("请至少填写 PU/柜号/提单号/备注，或上传单据");
        return;
      }
      if (!this.form.deliveryAddress || !this.form.deliveryAddress.trim()) {
        this.$message.warning("请填写送仓地址");
        return;
      }
      if (!this.form.railRampAddress || !this.form.railRampAddress.trim()) {
        this.$message.warning("请填写提柜港口/堆场");
        return;
      }
      if (!this.form.packageCount || this.form.packageCount <= 0) {
        this.$message.warning("请填写件数");
        return;
      }
      if (!this.form.deliverPlanDate || !this.form.deliverPlanSlot) {
        this.$message.warning("请选择期望送仓日期和上午/下午");
        return;
      }
      this.submitting = true;
      httpApi
        .trailerSubmit({
          puNo: this.form.puNo,
          containerNo: this.form.containerNo,
          blNo: this.form.blNo,
          deliveryAddress: this.form.deliveryAddress,
          railRampAddress: this.form.railRampAddress,
          packageCount: this.form.packageCount,
          cargoQty: this.form.cargoQty,
          unloadMode: this.form.unloadMode,
          deliverPlanDate: this.form.deliverPlanDate,
          deliverPlanSlot: this.form.deliverPlanSlot,
          remark: this.form.remark,
          fileUrls: this.uploaded.map((x) => x.url),
          fileNames: this.uploaded.map((x) => x.name),
        })
        .then((res) => {
          if (res.data.code === "ok") {
            const d = res.data.data || {};
            this.$message.success(
              "已提交：" +
                (d.planName || "") +
                " · " +
                (d.trailerStageTxt || "")
            );
            this.resetForm();
            this.loadList();
          } else {
            this.$message.warning(res.data.message || "提交失败");
          }
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    openRelease(row) {
      this.currentRow = row;
      this.releaseUploaded = [];
      this.releaseFileList = [];
      this.releaseVisible = true;
    },
    openConfirm(row) {
      this.currentRow = row;
      this.confirmForm = {
        deliverPlanDate: this.formatPlanDay(row.deliverPlanTime),
        deliverPlanSlot: row.deliverPlanSlot || "AM",
        railRampAddress: row.railRampAddress || row.pickupStation || "",
        packageCount: row.packageCount || undefined,
        unloadMode: row.unloadMode || "DROP",
      };
      if (this.confirmForm.deliverPlanDate === "-") {
        this.confirmForm.deliverPlanDate = "";
      }
      this.confirmVisible = true;
    },
    doRelease() {
      if (!this.currentRow) return;
      this.actionLoading = true;
      httpApi
        .trailerConfirm({
          id: this.currentRow.id,
          action: "RELEASE",
          fileUrls: this.releaseUploaded.map((x) => x.url),
          fileNames: this.releaseUploaded.map((x) => x.name),
        })
        .then((res) => {
          if (res.data.code === "ok") {
            this.$message.success("已进入「请确认时间」");
            this.releaseVisible = false;
            this.loadList();
          } else {
            this.$message.warning(res.data.message || "操作失败");
          }
        })
        .finally(() => {
          this.actionLoading = false;
        });
    },
    doConfirmTime() {
      if (!this.currentRow) return;
      if (!this.confirmForm.deliverPlanDate || !this.confirmForm.deliverPlanSlot) {
        this.$message.warning("请选择日期和上午/下午");
        return;
      }
      this.actionLoading = true;
      httpApi
        .trailerConfirm({
          id: this.currentRow.id,
          action: "CONFIRM_TIME",
          deliverPlanDate: this.confirmForm.deliverPlanDate,
          deliverPlanSlot: this.confirmForm.deliverPlanSlot,
          railRampAddress: this.confirmForm.railRampAddress,
          packageCount: this.confirmForm.packageCount,
          unloadMode: this.confirmForm.unloadMode,
        })
        .then((res) => {
          if (res.data.code === "ok") {
            this.$message.success("已确认，等待派车");
            this.confirmVisible = false;
            this.loadList();
          } else {
            this.$message.warning(res.data.message || "操作失败");
          }
        })
        .finally(() => {
          this.actionLoading = false;
        });
    },
    loadList() {
      this.loading = true;
      httpApi
        .trailerList({
          params: { page: this.page, rows: this.rowsPerPage },
        })
        .then((res) => {
          if (res.data.code === "ok") {
            this.rows = (res.data.data && res.data.data.rows) || [];
            this.total = (res.data.data && res.data.data.total) || 0;
          } else {
            this.$message.warning(res.data.message || "查询失败");
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onPage(p) {
      this.page = p;
      this.loadList();
    },
    onSize(s) {
      this.rowsPerPage = s;
      this.page = 1;
      this.loadList();
    },
  },
};
</script>
